/**
 * F29 (Wave 2C) — SuspendTeamDialog.
 *
 * Composes `@bravemove/ui-web/Modal` + react-hook-form + zodResolver to deliver
 * a destructive suspend confirmation with:
 *   - mandatory reason (Textarea, 1–500 chars per `SuspendTeamInputSchema`)
 *   - type-to-confirm phrase "SUSPEND" (defence-in-depth)
 *   - bulleted consequence list ("hidden from search", "mutations refused",
 *     "members keep read access")
 *
 * The shipped F02 `@bravemove/ui-web/SuspendDialog` primitive uses a pre-canned
 * Select for the reason; F29 D11 wants free-form text that's saved into the
 * member-visible audit log + suspended-team banner. We therefore compose the
 * lower-level Modal directly here.
 *
 * Pattern mirrors F11's B2B account suspend dialog at
 * `apps/admin/src/pages/b2b-accounts/B2bAccountDetailPage.tsx` with the
 * type-to-confirm phrase added on top per F13 destructive-action convention.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import { SuspendTeamInputSchema, type SuspendTeamInput } from '@bravemove/types';

export interface SuspendTeamDialogProps {
  readonly open: boolean;
  readonly teamName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: SuspendTeamInput) => void;
}

interface SuspendFormValues {
  readonly reason: string;
}

export function SuspendTeamDialog({
  open,
  teamName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: SuspendTeamDialogProps) {
  const { t } = useI18n();
  const CONFIRM_PHRASE = t('admin:team.admin.suspend_dialog.confirm_phrase');

  const localSchema = z.object({
    reason: SuspendTeamInputSchema.shape.reason,
  });

  const form = useForm<SuspendFormValues>({
    resolver: zodResolver(localSchema),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const [typed, setTyped] = useState('');

  // Reset state on dialog (re)open — intentional setState-in-effect "form
  // reset on dialog open" pattern (mirrors F02 SuspendDialog primitive).
  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTyped('');
    }
  }, [open, form]);

  const phraseMatches = typed.trim() === CONFIRM_PHRASE;
  const canConfirm = phraseMatches && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void form.handleSubmit((v) => onSubmit({ reason: v.reason }))();
  };

  // ICU `{teamName}` interpolation does NOT fire reliably in test env (vitest
  // jsdom — same trap F23's CommissionStatementDetailPage hit; see
  // `apps/admin/src/pages/commission-statements/[statementId].tsx`). Resolve
  // the title manually here and pass the result string to <Modal>.
  const interpolated = t('admin:team.admin.suspend_dialog.title', {
    teamName,
    defaultValue: 'Suspend {teamName}?',
  }) as string;
  const titleText = interpolated.includes('{teamName}') ? `Suspend ${teamName}?` : interpolated;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleText}
      description={t('admin:team.admin.suspend_dialog.body')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:team.admin.suspend_dialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
            data-testid="suspend-team-confirm"
          >
            {t('admin:team.admin.suspend_dialog.confirm_button')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
          <p className="text-[10.5px] font-mono uppercase tracking-wider text-warning font-bold mb-2">
            {t('admin:team.admin.suspend_dialog.consequences_heading')}
          </p>
          <ul className="list-disc ps-5 space-y-1 text-[13px] text-ink">
            <li>{t('admin:team.admin.suspend_dialog.consequence_hidden')}</li>
            <li>{t('admin:team.admin.suspend_dialog.consequence_no_mutations')}</li>
            <li>{t('admin:team.admin.suspend_dialog.consequence_read_access')}</li>
          </ul>
        </div>

        <FormField
          label={t('admin:team.admin.suspend_dialog.reason_label')}
          helper={t('admin:team.admin.suspend_dialog.reason_helper')}
          error={form.formState.errors.reason?.message}
          required
        >
          <Textarea
            rows={4}
            maxLength={500}
            showCounter
            data-testid="suspend-team-reason"
            {...form.register('reason')}
          />
        </FormField>

        <FormField
          label={(() => {
            const raw = t('admin:team.admin.suspend_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            }) as string;
            return raw.includes('{phrase}') ? `Type ${CONFIRM_PHRASE} to confirm` : raw;
          })()}
        >
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            invalid={typed.length > 0 && !phraseMatches}
            autoComplete="off"
            data-testid="suspend-team-confirm-phrase"
            aria-label={t('admin:team.admin.suspend_dialog.type_to_confirm_label', {
              phrase: CONFIRM_PHRASE,
              defaultValue: 'Type {phrase} to confirm',
            })}
          />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}
